import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  exportFormSubmissions,
  fetchSubmissionsExportColumns,
} from "../../../api/forms";
import { ApiError } from "../../../api/client";
import { unauthorizedMessage } from "../../../lib/auth/unauthorizedMessage";
import type {
  SubmissionsExportColumns,
  SubmissionsExportFormat,
  SubmissionsExportMetaKey,
} from "../../../types/submission";
import { AdminDateRangePicker } from "../../../components/admin/AdminDateRangePicker";
import { SubmissionsExcelSync } from "../../../components/admin/SubmissionsExcelSync";
import { SubmissionsExportMenu } from "../../../components/admin/SubmissionsExportMenu";
import {
  resolveExportFieldParams,
  resolveExportStaffColumnParams,
} from "../../../lib/forms/submissionsExport";

interface FormSubmissionsExportProps {
  formId: number;
  from: string;
  to: string;
  onRangeChange: (range: { from: string; to: string }) => void;
  onError: (message: string | null) => void;
  extras?: ReactNode;
  includeStaffColumns?: boolean;
  enableExcelSync?: boolean;
}

type ExportFormat = SubmissionsExportFormat;

const EMPTY_COLUMNS: SubmissionsExportColumns = {
  meta: [],
  fields: [],
  staffColumns: [],
};

export function FormSubmissionsExport({
  formId,
  from,
  to,
  onRangeChange,
  onError,
  extras,
  includeStaffColumns = false,
  enableExcelSync = false,
}: FormSubmissionsExportProps) {
  const [columns, setColumns] =
    useState<SubmissionsExportColumns>(EMPTY_COLUMNS);
  const [columnsLoading, setColumnsLoading] = useState(true);
  const [columnsError, setColumnsError] = useState<string | null>(null);
  const [format, setFormat] = useState<ExportFormat>("xlsx");
  const [selectedMetaKeys, setSelectedMetaKeys] = useState<
    Set<SubmissionsExportMetaKey>
  >(() => new Set());
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [selectedStaffColumnIds, setSelectedStaffColumnIds] = useState<
    Set<number>
  >(() => new Set());
  const [exporting, setExporting] = useState(false);

  const metaKeys = useMemo(
    () => columns.meta.map((column) => column.key),
    [columns.meta],
  );
  const fieldIds = useMemo(
    () => columns.fields.map((field) => field.id),
    [columns.fields],
  );
  const staffColumnIds = useMemo(
    () =>
      includeStaffColumns
        ? (columns.staffColumns ?? []).map((column) => column.id)
        : [],
    [columns.staffColumns, includeStaffColumns],
  );

  useEffect(() => {
    let cancelled = false;

    setColumnsLoading(true);
    setColumnsError(null);
    setColumns(EMPTY_COLUMNS);
    setSelectedMetaKeys(new Set());
    setSelectedFieldIds(new Set());
    setSelectedStaffColumnIds(new Set());

    void fetchSubmissionsExportColumns(formId)
      .then((nextColumns) => {
        if (cancelled) return;
        setColumns(nextColumns);
        setSelectedFieldIds(
          new Set(nextColumns.fields.map((field) => field.id)),
        );
        setSelectedStaffColumnIds(
          includeStaffColumns
            ? new Set(
                (nextColumns.staffColumns ?? []).map((column) => column.id),
              )
            : new Set(),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setColumnsError(
            err.message || `Не удалось загрузить столбцы (${err.status})`,
          );
        } else {
          setColumnsError("Не удалось загрузить столбцы экспорта");
        }
      })
      .finally(() => {
        if (!cancelled) setColumnsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formId, includeStaffColumns]);

  const allMetaSelected =
    metaKeys.length > 0 && selectedMetaKeys.size === metaKeys.length;
  const allFieldsSelected =
    fieldIds.length > 0 && selectedFieldIds.size === fieldIds.length;
  const allStaffSelected =
    staffColumnIds.length === 0 ||
    staffColumnIds.every((id) => selectedStaffColumnIds.has(id));
  const allColumnsSelected =
    (metaKeys.length === 0 || allMetaSelected) &&
    (fieldIds.length === 0 || allFieldsSelected) &&
    allStaffSelected;
  const noneSelected =
    selectedMetaKeys.size === 0 &&
    selectedFieldIds.size === 0 &&
    selectedStaffColumnIds.size === 0;
  const selectedCount =
    selectedMetaKeys.size + selectedFieldIds.size + selectedStaffColumnIds.size;
  const totalCount = metaKeys.length + fieldIds.length + staffColumnIds.length;

  const toggleMetaKey = (
    metaKey: SubmissionsExportMetaKey,
    checked: boolean,
  ) => {
    setSelectedMetaKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(metaKey);
      else next.delete(metaKey);
      return next;
    });
  };

  const toggleField = (fieldId: number, checked: boolean) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(fieldId);
      else next.delete(fieldId);
      return next;
    });
  };

  const toggleStaffColumn = (columnId: number, checked: boolean) => {
    setSelectedStaffColumnIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(columnId);
      else next.delete(columnId);
      return next;
    });
  };

  const selectAllColumns = () => {
    setSelectedMetaKeys(new Set(metaKeys));
    setSelectedFieldIds(new Set(fieldIds));
    setSelectedStaffColumnIds(new Set(staffColumnIds));
  };

  const clearColumnSelection = () => {
    setSelectedMetaKeys(new Set());
    setSelectedFieldIds(new Set());
    setSelectedStaffColumnIds(new Set());
  };

  const handleExport = async (orderedFieldIds: number[]) => {
    onError(null);
    setExporting(true);

    try {
      const meta =
        selectedMetaKeys.size > 0
          ? metaKeys.filter((key) => selectedMetaKeys.has(key))
          : undefined;
      const { fieldIds: exportFieldIds, order } = resolveExportFieldParams(
        fieldIds,
        orderedFieldIds,
        selectedFieldIds,
      );

      const exportStaffColumns = includeStaffColumns
        ? resolveExportStaffColumnParams(staffColumnIds, selectedStaffColumnIds)
        : undefined;

      await exportFormSubmissions(formId, {
        format,
        from: from || undefined,
        to: to || undefined,
        metaKeys: meta,
        fieldIds: exportFieldIds,
        order,
        staffColumns: exportStaffColumns,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          onError(unauthorizedMessage());
        } else if (err.status === 403) {
          onError("Нет доступа к экспорту. Нужен scope manager или admin.");
        } else {
          onError(err.message || `Не удалось экспортировать (${err.status})`);
        }
      } else {
        onError("Не удалось скачать файл экспорта");
      }
    } finally {
      setExporting(false);
    }
  };

  if (columnsLoading) {
    return (
      <>
        <p className="font-onest text-xs text-slate-500">
          Загрузка столбцов экспорта…
        </p>
        {extras}
      </>
    );
  }

  if (columnsError) {
    return (
      <>
        <p className="font-onest text-xs text-red-600">{columnsError}</p>
        {extras}
      </>
    );
  }

  if (totalCount === 0) {
    return extras ?? null;
  }

  return (
    <SubmissionsExportMenu
      idPrefix="export"
      format={format}
      onFormatChange={setFormat}
      columns={columns}
      selectedMetaKeys={selectedMetaKeys}
      selectedFieldIds={selectedFieldIds}
      selectedStaffColumnIds={selectedStaffColumnIds}
      onToggleMeta={toggleMetaKey}
      onToggleField={toggleField}
      onToggleStaffColumn={toggleStaffColumn}
      onSelectAll={selectAllColumns}
      onClearAll={clearColumnSelection}
      selectedCount={selectedCount}
      totalCount={totalCount}
      allSelected={allColumnsSelected}
      exporting={exporting}
      downloadDisabled={noneSelected}
      onDownload={(orderedFieldIds) => void handleExport(orderedFieldIds)}
      hint="Порядок в файле: № → служебные столбцы → поля формы → пометки. Поля можно перетащить. Служебные столбцы и пометки — только если отмечены."
      toolbarExtra={
        enableExcelSync
          ? (orderedFieldIds) => (
              <SubmissionsExcelSync
                formId={formId}
                catalogFieldIds={fieldIds}
                orderedFieldIds={orderedFieldIds}
                selectedFieldIds={selectedFieldIds}
                selectedMetaKeys={metaKeys.filter((key) =>
                  selectedMetaKeys.has(key),
                )}
                staffColumnIds={staffColumnIds}
                selectedStaffColumnIds={selectedStaffColumnIds}
                allowStaffMarks={includeStaffColumns}
                onError={onError}
              />
            )
          : undefined
      }
    >
      <AdminDateRangePicker from={from} to={to} onChange={onRangeChange} />
      {extras}
    </SubmissionsExportMenu>
  );
}
